import { type ReactNode, useMemo } from "react"
import { Search, Tag, Folder, FileText, Pencil, FolderOpen, Trash2, Copy, Pin, PinOff, FileDown, X, ExternalLink, Bookmark, BookmarkX, Loader2, Move, Eye, PanelLeft, Settings, Link, Code2, Plus, MessageSquare, ChevronsDownUp, ChevronsUpDown } from "lucide-react"
import { ADD_TO_CURRENT_CHAT_LABEL, AddToCurrentChatIcon } from "@/lib/add-to-current-chat"
import {
  CREATE_IN_FOLDER_ACTIONS,
  CREATE_IN_FOLDER_SUBMENU_LABEL,
  type CreateInFolderActionId,
} from "@/lib/create-in-folder"
import { toast } from "sonner"
import { useIntegrations } from "@/hooks/use-integrations"
import { useWorkspace } from "@/providers/workspace-provider"
import { absoluteAppUrl, absoluteDocumentUrlForPathOrId } from "@/lib/app-routes"
import type { ExtensionMenuContext as ExtMenuCtx, ExtensionMenuItem } from "@/types/integration"
import { isDocumentsRootMenu } from "@/lib/browse-folder-focus"
import {
  BrowseDocumentsMenuBlock,
  BrowseFolderVisibilityMenuBlock,
  BrowseShowAllHiddenRow,
} from "@/components/features/browse-folder-context-menu-blocks"
import { useBrowseFolderFocus } from "@/providers/browse-folder-focus-provider"
import { EntityContextMenu, type EntityContextMenuEntry } from "@/components/features/entity-context-menu"
import { extractTodoIdFromPath } from "@/lib/todo-id-from-path"
import { openInCursorWorkspace } from "@/hooks/use-open-in-cursor"
import { QUIET_MENU_DESTRUCTIVE_ITEM_CLASS, QUIET_OVERFLOW_MENU_LABEL_CLASS } from "@/lib/menu-chrome"
import { testIds } from "@/lib/test-ids"

/**
 * Shared context menu for file/folder actions. Used by the sidebar tree and the main
 * panel folder view (same menu for both). The document main view 3-dots menu is a
 * superset that adds tab-specific and lifecycle actions (e.g. Undo, Archive,
 * Download PDF) when a document is open in a tab.
 */

interface ContextMenuProps {
  x: number
  y: number
  onClose: () => void
  context: {
    type: "tag" | "document" | "folder" | "project" | "tab" | "extension-entity" | "flow"
    value: string
    tags?: string[]
    path?: string
    filePath?: string
    documentId?: string
    // Tab-specific properties
    tabId?: string
    tabType?: string  // NEW: Tab type (e.g., 'explorer', 'home', 'work-spec')
    isPinned?: boolean
    hasFilePath?: boolean
    isListTab?: boolean
    isNewTab?: boolean
    /** Last remaining Home tab - Close is a no-op, hide the item. */
    hideCloseTab?: boolean
    /** Extension context menu: integration id, entity type, and entity payload (e.g. Basecamp todo) */
    extensionId?: string
    extensionEntityType?: string
    extensionEntity?: unknown
    /** Workspace Documents sidebar when content_root is unset - gate paths that need a folder path */
    missingContentRoot?: boolean
    workspaceId?: string
    /** Workspace tab group this tab belongs to. */
    workspaceGroupId?: string
    groupCollapsed?: boolean
    /** When type === "flow" */
    flowId?: string
    flowEnabled?: boolean
    /** Set when opening menu on a parent with hidden children (e.g. clients/). */
    browseHiddenChildCount?: number
    browseHiddenLabel?: string
    /** Precomputed app pathname for tab Copy URL (from buildAppUrl). */
    shareUrl?: string
    /** Sidebar shortcut pin, distinct from tab.isPinned (tab bar pin). */
    sidebarPinned?: boolean
  }
  onSearchIn?: (payload: {
    query: string
    context: ContextMenuProps["context"]
    /** Full folder path when Search in is used on a folder */
    path?: string
  }) => void
  onCreateFile?: (folderPath: string) => void
  onCreateFolder?: (parentPath: string) => void
  /** Import local .md / .txt into this folder */
  onImportTextFiles?: (folderPath: string, workspaceId?: string) => void
  onOpen?: (documentId: string) => void
  onPreview?: (item: { type: 'document' | 'folder'; id: string; path: string; name: string }) => void
  onRename?: (item: { type: "file" | "folder"; id: string; currentName: string; path: string }) => void
  onMove?: (item: { type: "file" | "folder"; id: string; currentPath: string }) => void
  onDelete?: (item: { type: "file" | "folder"; id: string; name: string; path: string }) => void
  onAddToChat?: (item: { type: "file" | "folder"; id: string; path: string; title?: string }) => void
  onPinToSidebar?: (idOrPath: string, isPinned: boolean, targetType?: 'document' | 'folder') => Promise<void>
  isPinned?: boolean
  // Tab-specific handlers
  onTabShowFileLocation?: (tabId: string) => void
  onTabRename?: (tabId: string) => void
  onTabMove?: (tabId: string) => void
  onTabExportPDF?: (tabId: string) => void
  onTabAddToChat?: (tabId: string) => void
  onTabToggleGroupCollapsed?: (workspaceGroupId: string) => void
  onTabPinToggle?: (tabId: string) => void
  onTabClose?: (tabId: string) => void
  onTabCloseOthers?: (tabId: string) => void
  onTabMoveToWindow?: (tabId: string) => void
  onTabCopyToWindow?: (tabId: string) => void
  onTabToggleBookmark?: (tabId: string) => Promise<void>
  tabBookmarkStatus?: { isBookmarked: boolean | null; isToggling: boolean }
  onFlowToggleEnable?: (flowId: string, nextEnabled: boolean) => void | Promise<void>
  onFlowTriggerRun?: (flowId: string) => void | Promise<void>
}

export function ContextMenu({
  x,
  y,
  onClose,
  context,
  onSearchIn,
  onCreateFile,
  onCreateFolder,
  onImportTextFiles,
  onOpen,
  onPreview,
  onRename,
  onMove,
  onDelete,
  onAddToChat,
  onPinToSidebar,
  isPinned,
  onTabShowFileLocation,
  onTabRename,
  onTabMove,
  onTabExportPDF,
  onTabAddToChat,
  onTabToggleGroupCollapsed,
  onTabPinToggle,
  onTabClose,
  onTabCloseOthers,
  onTabMoveToWindow,
  onTabCopyToWindow,
  onTabToggleBookmark,
  tabBookmarkStatus,
  onFlowToggleEnable,
  onFlowTriggerRun,
}: ContextMenuProps) {
  const browseFocus = useBrowseFolderFocus()
  const { getIntegration } = useIntegrations()
  const { workspaces } = useWorkspace()
  const workspacesRoute = useMemo(
    () => workspaces.map((w) => ({ id: w.id, slug: w.slug, contentRoot: w.contentRoot })),
    [workspaces]
  )

  const handleSearchIn = (query: string, path?: string) => {
    onSearchIn?.({ query, context, path })
    onClose()
  }

  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} copied!`, {
        description: text,
        duration: 2000
      })
      onClose()
    } catch (err) {
      console.error(`Failed to copy ${label}:`, err)
      toast.error(`Failed to copy ${label}`)
    }
  }

  type RenderableMenuItem = {
    icon?: React.ReactNode
    label?: string
    action?: () => void
    separator?: boolean
    disabled?: boolean
    destructive?: boolean
    className?: string
    testId?: string
    custom?: ReactNode
    submenu?: RenderableMenuItem[]
  }

  /** Same Location group as the document viewer ... menu. Section label, not a nested flyout. */
  const locationGroup = (
    filePath: string,
    pin: { idOrPath: string; pinned: boolean; targetType: 'document' | 'folder' },
    opts?: { onShowFileLocation?: () => void },
  ): RenderableMenuItem[] => [
    {
      custom: (
        <div className={QUIET_OVERFLOW_MENU_LABEL_CLASS} role="presentation">
          Location
        </div>
      ),
    },
    {
      icon: <Folder className="h-4 w-4" />,
      label: "Show file location",
      action: () => {
        if (opts?.onShowFileLocation) opts.onShowFileLocation()
        else window.dispatchEvent(new CustomEvent('show-file-location', { detail: { filePath } }))
        onClose()
      },
    },
    {
      icon: <PanelLeft className="h-4 w-4" />,
      label: "Reveal in sidebar",
      action: () => {
        window.dispatchEvent(new CustomEvent('reveal-in-sidebar', { detail: { filePath } }))
        onClose()
      },
    },
    {
      icon: pin.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />,
      label: pin.pinned ? "Unpin from sidebar" : "Pin to sidebar",
      action: () => {
        if (onPinToSidebar) {
          void onPinToSidebar(pin.idOrPath, pin.pinned, pin.targetType)
        } else {
          window.dispatchEvent(new CustomEvent('sidebar-pin-toggle', {
            detail: { idOrPath: pin.idOrPath, targetType: pin.targetType },
          }))
        }
        onClose()
      },
    },
  ]

  const folderCreateSubmenu = (
    run: (id: CreateInFolderActionId) => void,
  ): RenderableMenuItem => ({
    icon: <Plus className="h-4 w-4" />,
    label: CREATE_IN_FOLDER_SUBMENU_LABEL,
    submenu: CREATE_IN_FOLDER_ACTIONS.map((action) => {
      const Icon = action.icon
      return {
        icon: <Icon className="h-4 w-4" />,
        label: action.label,
        action: () => run(action.id),
      }
    }),
  })

  const mapExtensionMenuToItems = (
    extItems: ExtensionMenuItem[],
    entity: unknown,
    extCtx: ExtMenuCtx
  ): RenderableMenuItem[] => {
    const out: RenderableMenuItem[] = []
    for (const it of extItems) {
      if (it.separatorBefore) {
        out.push({ separator: true })
      }
      const isDisabled = typeof it.disabled === "function" ? it.disabled(entity, extCtx) : !!it.disabled
      const Icon = it.icon
      out.push({
        icon: Icon ? <Icon className="h-4 w-4" /> : undefined,
        label: it.label,
        action: () => {
          it.action(entity, extCtx)
          onClose()
        },
        disabled: isDisabled,
        destructive: !!it.destructive,
        className: it.destructive ? QUIET_MENU_DESTRUCTIVE_ITEM_CLASS : undefined,
      })
    }
    return out
  }

  const getMenuItems = (): RenderableMenuItem[] => {
    switch (context.type) {
      case "tag":
        return [
          {
            icon: <Search className="h-4 w-4" />,
            label: `Search in #${context.value}`,
            action: () => handleSearchIn(`tag:${context.value}`),
          },
          {
            icon: <Tag className="h-4 w-4" />,
            label: `Show all #${context.value} documents`,
            action: () => handleSearchIn(`tag:${context.value}`),
          },
        ]
      case "document":
        {
          const documentId = context.documentId || context.filePath || context.path || context.value
          const filePath = context.filePath || context.path || context.value
          const fileName = context.value
          const todoIdFromPath = extractTodoIdFromPath(filePath)
          const docUuid =
            typeof context.documentId === "string" &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
              context.documentId,
            )
              ? context.documentId
              : null

          return [
            {
              icon: <FileText className="h-4 w-4" />,
              label: "Open",
              action: () => {
                onOpen?.(documentId)
                onClose()
              },
            },
            {
              icon: <Eye className="h-4 w-4" />,
              label: "Preview",
              action: () => {
                onPreview?.({ type: 'document', id: documentId, path: filePath, name: fileName })
                onClose()
              },
            },
            ...(todoIdFromPath
              ? [
                  {
                    icon: <Code2 className="h-4 w-4" />,
                    label: "Open in Cursor",
                    action: () => {
                      onClose()
                      void openInCursorWorkspace({
                        todoId: todoIdFromPath,
                        documentId: docUuid,
                        todoTitle: fileName,
                      })
                    },
                  },
                ]
              : []),
            { separator: true },
            ...locationGroup(filePath, {
              idOrPath: documentId,
              pinned: !!isPinned,
              targetType: 'document',
            }),
            { separator: true },
            {
              icon: <Pencil className="h-4 w-4" />,
              label: "Rename",
              action: () => {
                onRename?.({
                  type: "file",
                  id: documentId,
                  currentName: fileName,
                  path: filePath
                })
                onClose()
              },
            },
            {
              icon: <FolderOpen className="h-4 w-4" />,
              label: "Move",
              action: () => {
                onMove?.({
                  type: "file",
                  id: documentId,
                  currentPath: filePath
                })
                onClose()
              },
            },
            ...(browseFocus
              ? [
                  {
                    custom: (
                      <BrowseFolderVisibilityMenuBlock
                        mode={browseFocus.getFolderMode(filePath)}
                        onModeChange={(mode) => browseFocus.setFolderMode(filePath, mode)}
                        focusBrowseEnabled={browseFocus.focusBrowse}
                      />
                    ),
                  },
                ]
              : []),
            { separator: true },
            {
              icon: <Copy className="h-4 w-4" />,
              label: "Copy filename",
              action: () => handleCopyToClipboard(fileName, "Filename"),
            },
            {
              icon: <Copy className="h-4 w-4" />,
              label: "Copy path",
              action: () => {
                const pathToCopy = filePath.startsWith('/') ? filePath : `/${filePath}`
                handleCopyToClipboard(pathToCopy, "Path")
              },
            },
            {
              icon: <Link className="h-4 w-4" />,
              label: "Copy URL",
              action: () => {
                handleCopyToClipboard(
                  absoluteDocumentUrlForPathOrId(documentId, workspacesRoute),
                  "URL"
                )
              },
            },
            { separator: true },
            {
              icon: <AddToCurrentChatIcon className="h-4 w-4" />,
              label: ADD_TO_CURRENT_CHAT_LABEL,
              action: () => {
                onAddToChat?.({
                  type: "file",
                  id: documentId,
                  path: filePath
                })
                onClose()
              },
            },
            { separator: true },
            {
              icon: <Trash2 className="h-4 w-4" />,
              label: "Delete",
              action: () => {
                onDelete?.({
                  type: "file",
                  id: documentId,
                  name: fileName,
                  path: filePath
                })
                onClose()
              },
              destructive: true,
            },
          ]
        }
      case "folder":
        {
          const explicitPath = (context.path ?? "").trim()
          const missingRoot = context.missingContentRoot === true && explicitPath === ""
          const folderPath = explicitPath || context.value
          const folderName = context.value

          const promptContentRoot = () => {
            toast.message("Workspace content path unavailable", {
              description: "New workspaces get an automatic path. If this persists, check Workspace settings or ask an admin.",
            })
            window.dispatchEvent(
              new CustomEvent("open-settings", {
                detail: {
                  tab: "organizations",
                  ...(context.workspaceId ? { organizationId: context.workspaceId } : {}),
                },
              })
            )
            onClose()
          }

          if (missingRoot) {
            return [
              folderCreateSubmenu(() => promptContentRoot()),
              { separator: true },
              {
                icon: <Settings className="h-4 w-4" />,
                label: "Workspace settings",
                action: () => {
                  window.dispatchEvent(
                    new CustomEvent("open-settings", {
                      detail: {
                        tab: "organizations",
                        ...(context.workspaceId ? { organizationId: context.workspaceId } : {}),
                      },
                    })
                  )
                  onClose()
                },
              },
            ]
          }

          const folderTodoId = extractTodoIdFromPath(folderPath)

          return [
            {
              icon: <Search className="h-4 w-4" />,
              label: `Search in ${context.value}`,
              action: () => handleSearchIn(`path:${folderPath}`, folderPath),
            },
            {
              icon: <Eye className="h-4 w-4" />,
              label: "Preview",
              action: () => {
                onPreview?.({ type: 'folder', id: folderPath, path: folderPath, name: folderName })
                onClose()
              },
            },
            ...(folderTodoId
              ? [
                  {
                    icon: <Code2 className="h-4 w-4" />,
                    label: "Open in Cursor",
                    action: () => {
                      onClose()
                      void openInCursorWorkspace({
                        todoId: folderTodoId,
                        todoTitle: folderName,
                      })
                    },
                  },
                ]
              : []),
            { separator: true },
            ...locationGroup(folderPath, {
              idOrPath: folderPath,
              pinned: !!isPinned,
              targetType: 'folder',
            }),
            { separator: true },
            folderCreateSubmenu((id) => {
              if (id === 'document') onCreateFile?.(folderPath)
              else if (id === 'import') onImportTextFiles?.(folderPath, context.workspaceId)
              else onCreateFolder?.(folderPath)
              onClose()
            }),
            { separator: true },
            {
              icon: <Pencil className="h-4 w-4" />,
              label: "Rename",
              action: () => {
                onRename?.({
                  type: "folder",
                  id: folderPath,
                  currentName: folderName,
                  path: folderPath
                })
                onClose()
              },
            },
            {
              icon: <Move className="h-4 w-4" />,
              label: "Move to…",
              action: () => {
                onMove?.({
                  type: "folder",
                  id: folderPath,
                  currentPath: folderPath
                })
                onClose()
              },
            },
            ...(!isDocumentsRootMenu(folderName, folderPath)
              ? [
                  { separator: true },
                  {
                    icon: <Trash2 className="h-4 w-4" />,
                    label: "Delete folder",
                    action: () => {
                      onDelete?.({
                        type: "folder",
                        id: folderPath,
                        name: folderName,
                        path: folderPath,
                      })
                      onClose()
                    },
                    destructive: true,
                  },
                ]
              : []),
            ...(browseFocus && isDocumentsRootMenu(folderName, folderPath)
              ? [
                  {
                    custom: (
                      <BrowseDocumentsMenuBlock
                        focusBrowse={browseFocus.focusBrowse}
                        onFocusBrowseChange={browseFocus.setFocusBrowse}
                        inactiveDays={browseFocus.inactiveDays}
                        onInactiveDaysChange={(d) => browseFocus.setInactiveDays(d)}
                        showActivityInTree={browseFocus.showActivityInTree}
                        onShowActivityInTreeChange={browseFocus.setShowActivityInTree}
                        onOpenBrowsePreferences={() => {
                          window.dispatchEvent(
                            new CustomEvent("open-settings", {
                              detail: { tab: "ui", focusSection: "browse-sidebar" },
                            })
                          )
                          onClose()
                        }}
                      />
                    ),
                  },
                ]
              : []),
            ...(browseFocus &&
            browseFocus.focusBrowse &&
            (context.browseHiddenChildCount ?? 0) > 0
              ? [
                  {
                    custom: (
                      <BrowseShowAllHiddenRow
                        hiddenCount={context.browseHiddenChildCount ?? 0}
                        label={context.browseHiddenLabel}
                        onShowAll={() => {
                          if (browseFocus.focusBrowse && folderPath) {
                            browseFocus.revealBrowseParent(folderPath)
                          } else {
                            browseFocus.restoreBrowseFocusFilter()
                          }
                          onClose()
                        }}
                      />
                    ),
                  },
                ]
              : []),
            ...(browseFocus &&
            !isDocumentsRootMenu(folderName, folderPath) &&
            context.type === "folder"
              ? [
                  {
                    custom: (
                      <BrowseFolderVisibilityMenuBlock
                        mode={browseFocus.getFolderMode(folderPath)}
                        onModeChange={(mode) => browseFocus.setFolderMode(folderPath, mode)}
                        focusBrowseEnabled={browseFocus.focusBrowse}
                      />
                    ),
                  },
                ]
              : []),
            ...(context.workspaceId
              ? [
                  { separator: true },
                  {
                    icon: <Settings className="h-4 w-4" />,
                    label: "Settings",
                    action: () => {
                      window.dispatchEvent(
                        new CustomEvent("open-settings", {
                          detail: { tab: "organizations", organizationId: context.workspaceId },
                        })
                      )
                      onClose()
                    },
                  },
                ]
              : []),
            { separator: true },
            {
              icon: <AddToCurrentChatIcon className="h-4 w-4" />,
              label: ADD_TO_CURRENT_CHAT_LABEL,
              action: () => {
                onAddToChat?.({
                  type: "folder",
                  id: folderPath,
                  path: folderPath,
                  title: folderName,
                })
                onClose()
              },
            },
          ]
        }
      case "project":
        {
          const folderPath = context.path || context.value
          const folderName = context.value

          return [
            {
              icon: <Search className="h-4 w-4" />,
              label: `Search in ${context.value}`,
              action: () => handleSearchIn(`project:${context.value}`),
            },
            { separator: true },
            folderCreateSubmenu((id) => {
              if (id === 'document') onCreateFile?.(folderPath)
              else if (id === 'import') onImportTextFiles?.(folderPath, context.workspaceId)
              else onCreateFolder?.(folderPath)
              onClose()
            }),
            { separator: true },
            {
              icon: <Pencil className="h-4 w-4" />,
              label: "Rename",
              action: () => {
                onRename?.({
                  type: "folder",
                  id: folderPath,
                  currentName: folderName,
                  path: folderPath
                })
                onClose()
              },
            },
            {
              icon: <Move className="h-4 w-4" />,
              label: "Move to…",
              action: () => {
                onMove?.({
                  type: "folder",
                  id: folderPath,
                  currentPath: folderPath
                })
                onClose()
              },
            },
            { separator: true },
            {
              icon: <Trash2 className="h-4 w-4" />,
              label: "Delete folder",
              action: () => {
                onDelete?.({
                  type: "folder",
                  id: folderPath,
                  name: folderName,
                  path: folderPath,
                })
                onClose()
              },
              destructive: true,
            },
            { separator: true },
            {
              icon: <AddToCurrentChatIcon className="h-4 w-4" />,
              label: ADD_TO_CURRENT_CHAT_LABEL,
              action: () => {
                onAddToChat?.({
                  type: "folder",
                  id: folderPath,
                  path: folderPath,
                  title: folderName,
                })
                onClose()
              },
            },
          ]
        }
      case "tab":
        {
          const tabId = context.tabId || context.documentId || context.value
          const filePath = context.filePath || context.path
          const hasFilePath = context.hasFilePath ?? !!filePath
          const tabType = context.tabType
          // Check if it's a special tab that shouldn't have document-specific actions (but can still be closed/pinned)
          const isSpecialTab = tabType === 'explorer' || tabType === 'home' || tabType === 'graph-view'
          const isChatTab = tabType === 'chat-conversation'
          // isListTab is for backwards compatibility - but Explorer tabs should behave like normal tabs now
          // Only block document-specific actions (rename, move, PDF) for Explorer/home/graph tabs
          const isListTab = context.isListTab ?? false  // Don't auto-detect - rely on tabType instead
          const isNewTab = context.isNewTab ?? (tabId?.startsWith("new-") ?? false)
          const isPinned = context.isPinned ?? false

          const menuItems: RenderableMenuItem[] = []

          // Location group, same three actions as the document ... menu.
          // Explorer tabs are a folder; home and graph have no file path.
          const locationPath = (filePath || '').trim()
          const canShowLocation = !!locationPath && !isNewTab && tabType !== 'home' && tabType !== 'graph-view' && !isChatTab
          if (canShowLocation) {
            const pinTarget = tabType === 'explorer' ? 'folder' : 'document'
            menuItems.push(...locationGroup(locationPath, {
              idOrPath: pinTarget === 'folder' ? locationPath : (tabId || locationPath),
              pinned: context.sidebarPinned === true,
              targetType: pinTarget,
            }, pinTarget === 'document' && onTabShowFileLocation
              ? { onShowFileLocation: () => onTabShowFileLocation(tabId) }
              : undefined))
            menuItems.push({ separator: true })
          }

          // Rename (if has path and not special/new tab)
          if (hasFilePath && !isSpecialTab && !isNewTab) {
            menuItems.push({
              icon: <Pencil className="h-4 w-4" />,
              label: "Rename",
              action: () => {
                onTabRename?.(tabId)
                onClose()
              },
            })
          }

          // Move (if has path and not special/new tab)
          if (hasFilePath && !isSpecialTab && !isNewTab) {
            menuItems.push({
              icon: <FolderOpen className="h-4 w-4" />,
              label: "Move document",
              action: () => {
                onTabMove?.(tabId)
                onClose()
              },
            })
          }

          if (context.shareUrl) {
            menuItems.push({
              icon: <Link className="h-4 w-4" />,
              label: "Copy URL",
              action: () => handleCopyToClipboard(absoluteAppUrl(context.shareUrl!), "URL"),
            })
          }

          if ((hasFilePath && !isSpecialTab && !isNewTab) || context.shareUrl) {
            menuItems.push({ separator: true })
          }

          // Download PDF (if not special/new tab)
          if (!isSpecialTab && !isNewTab) {
            menuItems.push({
              icon: <FileDown className="h-4 w-4" />,
              label: "Download PDF",
              action: () => {
                onTabExportPDF?.(tabId)
                onClose()
              },
            })
            menuItems.push({ separator: true })
          }

          // Add to chat: file-backed tabs, extension tabs that opt in, or explorer folder tabs
          const integ = context.extensionId ? getIntegration(context.extensionId) : undefined
          const canAddToChatNoPath = !!(integ?.addToChatForTabWithoutPath && context.extensionEntityType)
          const explorerFolderPath =
            tabType === 'explorer' ? String(context.path || context.filePath || '').trim() : ''
          const canAddExplorerFolder = explorerFolderPath.length > 0 && !isNewTab
          if (
            ((hasFilePath || canAddToChatNoPath) && !isSpecialTab && !isNewTab) ||
            canAddExplorerFolder
          ) {
            menuItems.push({
              icon: <AddToCurrentChatIcon className="h-4 w-4" />,
              label: ADD_TO_CURRENT_CHAT_LABEL,
              action: () => {
                if (canAddExplorerFolder) {
                  window.dispatchEvent(
                    new CustomEvent('add-to-chat', {
                      detail: {
                        id: explorerFolderPath,
                        title: context.value,
                        name: context.value,
                        path: explorerFolderPath,
                        documentType: 'folder',
                        type: 'folder',
                        kind: 'folder',
                      },
                    }),
                  )
                } else {
                  onTabAddToChat?.(tabId)
                }
                onClose()
              },
            })
            menuItems.push({ separator: true })
          }

          // Extension-specific items (e.g. Basecamp Open in…, Copy URL) from Integration.entityContextMenus
          if (
            context.extensionId &&
            context.extensionEntityType &&
            context.extensionEntity != null &&
            integ?.enabled &&
            integ.configured
          ) {
            const decl = integ.entityContextMenus?.find((d) => d.entityType === context.extensionEntityType)
            if (decl) {
              const extCtx: ExtMenuCtx = { tabId, workspaceId: undefined }
              const extRendered = mapExtensionMenuToItems(decl.items(context.extensionEntity, extCtx), context.extensionEntity, extCtx)
              if (extRendered.length > 0) {
                menuItems.push(...extRendered)
                menuItems.push({ separator: true })
              }
            }
          }

          // Bookmark toggle (if document has Basecamp todo and handler is available)
          const shouldShowBookmark = onTabToggleBookmark && tabBookmarkStatus && tabBookmarkStatus.isBookmarked !== null
          if (shouldShowBookmark) {
            menuItems.push({
              icon: tabBookmarkStatus.isToggling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : tabBookmarkStatus.isBookmarked ? (
                <BookmarkX className="h-4 w-4" />
              ) : (
                <Bookmark className="h-4 w-4" />
              ),
              label: tabBookmarkStatus.isToggling
                ? (tabBookmarkStatus.isBookmarked ? 'Removing...' : 'Adding...')
                : (tabBookmarkStatus.isBookmarked ? 'Remove Bookmark' : 'Add Bookmark'),
              action: async () => {
                await onTabToggleBookmark(tabId)
                onClose()
              },
              disabled: tabBookmarkStatus.isToggling,
            })
            menuItems.push({ separator: true })
          }

          // Chat conversation tab: move to agent panel
          if (isChatTab && tabId) {
            const threadId = tabId.replace(/^chat-conversation-/, '')
            menuItems.push({
              icon: <MessageSquare className="h-4 w-4" />,
              label: 'Move to agent panel',
              action: () => {
                window.dispatchEvent(
                  new CustomEvent('chat-surface-move', {
                    detail: { threadId, target: 'agent-panel' },
                  }),
                )
                onClose()
              },
            })
            menuItems.push({ separator: true })
          }

          if (onTabToggleGroupCollapsed && context.workspaceGroupId) {
            const groupId = context.workspaceGroupId
            const collapsed = context.groupCollapsed === true
            menuItems.push({
              icon: collapsed
                ? <ChevronsUpDown className="h-4 w-4" />
                : <ChevronsDownUp className="h-4 w-4" />,
              label: collapsed ? "Expand group" : "Collapse group",
              testId: testIds.documentTabs.groupToggle(groupId),
              action: () => {
                onTabToggleGroupCollapsed(groupId)
                onClose()
              },
            })
            menuItems.push({ separator: true })
          }

          // Pin/Unpin (allow for all tabs including Explorer and Home)
          menuItems.push({
            icon: isPinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />,
            label: isPinned ? "Unpin tab" : "Pin tab",
            action: () => {
              onTabPinToggle?.(tabId)
              onClose()
            },
          })
          menuItems.push({ separator: true })

          if (!context.hideCloseTab) {
          menuItems.push({
            icon: <X className="h-4 w-4" />,
            label: "Close tab",
            action: () => {
              onTabClose?.(tabId)
              onClose()
            },
          })
          }

          // Close other tabs (allow for all tabs)
          menuItems.push({
            icon: <X className="h-4 w-4" />,
            label: "Close other tabs",
            action: () => {
              onTabCloseOthers?.(tabId)
              onClose()
            },
          })
          menuItems.push({ separator: true })

          // Move/Copy to new window (allow for all tabs)
          menuItems.push({
            icon: <ExternalLink className="h-4 w-4" />,
            label: "Move to new window",
            action: () => {
              onTabMoveToWindow?.(tabId)
              onClose()
            },
          })
          menuItems.push({
            icon: <Copy className="h-4 w-4" />,
            label: "Copy to new window",
            action: () => {
              onTabCopyToWindow?.(tabId)
              onClose()
            },
          })

          return menuItems
        }
      case "flow": {
        const flowId = context.flowId ?? context.value
        const enabled = context.flowEnabled ?? false
        const items: RenderableMenuItem[] = []
        if (onFlowToggleEnable) {
          items.push({
            label: enabled ? "Disable flow" : "Enable flow",
            action: async () => {
              await onFlowToggleEnable(flowId, !enabled)
              onClose()
            },
          } satisfies RenderableMenuItem)
        }
        if (onFlowTriggerRun) {
          items.push({
            label: "Run now",
            action: async () => {
              await onFlowTriggerRun(flowId)
              onClose()
            },
          } satisfies RenderableMenuItem)
        }
        return items
      }
      case "extension-entity": {
        const extId = context.extensionId
        const entType = context.extensionEntityType
        const entity = context.extensionEntity
        if (!extId || !entType || entity == null) {
          return []
        }
        const integ = getIntegration(extId)
        if (!integ?.enabled || !integ.configured) {
          return []
        }
        const decl = integ.entityContextMenus?.find((d) => d.entityType === entType)
        if (!decl) {
          return []
        }
        const extCtx: ExtMenuCtx = { tabId: context.tabId, workspaceId: undefined }
        return mapExtensionMenuToItems(decl.items(entity, extCtx), entity, extCtx)
      }
      default:
        return []
    }
  }

  const menuItems = getMenuItems()

  const entries: EntityContextMenuEntry[] = []
  for (const item of menuItems) {
    if (item.separator) {
      entries.push({ separator: true })
      continue
    }
    const row = item as RenderableMenuItem
    if (row.custom) {
      entries.push({ custom: row.custom })
      continue
    }
    if (row.submenu && row.submenu.length > 0) {
      entries.push({
        icon: row.icon,
        label: row.label ?? CREATE_IN_FOLDER_SUBMENU_LABEL,
        disabled: row.disabled,
        children: row.submenu.map((child) => {
          if (child.separator) return { separator: true as const }
          return {
            icon: child.icon,
            label: child.label ?? "",
            disabled: child.disabled,
            destructive: child.destructive,
            className: child.className,
            onSelect: () => {
              child.action?.()
            },
          }
        }),
      })
      continue
    }
    entries.push({
      icon: row.icon,
      label: row.label ?? "",
      disabled: row.disabled,
      destructive: row.destructive,
      className: row.className,
      testId: row.testId,
      onSelect: () => {
        row.action?.()
      },
    })
  }

  return (
    <EntityContextMenu
      x={x}
      y={y}
      onClose={onClose}
      entries={entries}
    />
  )
}

